// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Force settle overlay (non-dismissible)
// Forces the employee to close the cash session before continuing.
// Renders as a portal at z-[9998].
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useCashStore } from '@/store/cash-store';
import { apiClient } from '@/services/api-client';
import { useAppStore } from '@/store/app-store';

export default function ForceSettleOverlay() {
  const sessionData = useCashStore((s) => s.sessionData);
  const clearSession = useCashStore((s) => s.clearSession);
  const showToast = useAppStore((s) => s.showToast);

  const [isClosing, setIsClosing] = useState(false);
  const [status, setStatus] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const handleClose = async () => {
    setIsClosing(true);
    setStatus(null);

    try {
      // NF525: Use expected_cash_amount instead of 0 to avoid a fictitious gap.
      // The real cash count will be done by the manager in the backoffice.
      const expectedAmount = sessionData?.expected_cash_amount ?? 0;

      await apiClient.closeCashSession(
        expectedAmount,
        '[Auto] Fermeture depuis la PWA — montant attendu utilise, comptage a valider',
      );

      setStatus({ text: 'Session fermee avec succes !', type: 'success' });
      clearSession();

      // The next heartbeat will hide the overlay
    } catch (err) {
      const message = (err as Error).message || 'Echec de la fermeture';
      setStatus({ text: `Erreur: ${message}`, type: 'error' });
      showToast('Erreur fermeture caisse', 'error');
      setIsClosing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-surface/95 backdrop-blur-sm">
      <div className="text-center px-8 max-w-md animate-fade-in">
        {/* Warning icon */}
        <div className="mb-4">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-warning">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-3 tracking-wide">
          FERMETURE DE CAISSE
        </h1>

        {/* Message */}
        <p className="text-gray-300 text-sm mb-6 leading-relaxed">
          Les horaires d'ouverture sont depasses.<br />
          Vous devez fermer la session de caisse pour continuer.
        </p>

        {/* Session info */}
        <div className="bg-surface-card rounded-xl px-6 py-3 mb-6 inline-block">
          <span className="text-gray-400 text-xs">Session</span>
          <span className="text-white font-semibold ml-2">
            #{sessionData?.id ?? '---'}
          </span>
        </div>

        {/* Close button */}
        <div className="mb-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isClosing}
            className="btn-danger text-base py-3 px-8 font-bold tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isClosing ? 'Fermeture en cours...' : 'FERMER LA CAISSE'}
          </button>
        </div>

        {/* Status feedback */}
        {status && (
          <p className={`text-sm mb-3 ${status.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {status.text}
          </p>
        )}

        {/* Hint */}
        <p className="text-gray-500 text-xs">
          Un comptage manuel sera necessaire ulterieurement.
        </p>
      </div>
    </div>,
    document.body,
  );
}
